import { DeleteOutline, PlaylistRemove } from '@mui/icons-material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, type ActionMenuItem } from 'shared/ui/ActionMenu';

export type RemoveCustomListItemsMode = 'fromList' | 'fromCatalog';

interface RemoveCustomListItemsMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    onSelect: (mode: RemoveCustomListItemsMode) => void;
}

const iconSx = { width: '22px', height: '22px' } as const;

export const RemoveCustomListItemsMenu = memo(
    ({ anchorEl, open, onClose, onSelect }: RemoveCustomListItemsMenuProps) => {
        const { t } = useTranslation();

        const items: ActionMenuItem[] = useMemo(
            () => [
                {
                    id: 'from-list',
                    label: t('customLists.removeFromThisList'),
                    description: t('customLists.removeFromThisListHint'),
                    icon: <PlaylistRemove sx={iconSx} />,
                    onClick: () => onSelect('fromList'),
                    dataTestClass: 'remove-custom-list-items-from-list',
                },
                {
                    id: 'from-catalog',
                    label: t('customLists.removeFromCatalog'),
                    description: t('customLists.removeFromCatalogHint'),
                    icon: <DeleteOutline sx={iconSx} />,
                    tone: 'danger',
                    onClick: () => onSelect('fromCatalog'),
                    dataTestClass: 'remove-custom-list-items-from-catalog',
                },
            ],
            [onSelect, t],
        );

        return (
            <ActionMenu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                items={items}
                maxWidth={250}
                dataTestClass="remove-custom-list-items-menu"
            />
        );
    },
);

RemoveCustomListItemsMenu.displayName = 'RemoveCustomListItemsMenu';
